import { useEffect, useState, useRef } from "react";

interface PlacementResult {
    top?: number;
    left?: number;
    bottom?: number;
    right?: number;
}

export function useSmartPlacement(
    targetId: string,
    modalId: string,
    placement: "top" | "bottom",
    open: boolean,
    pinned: boolean,
    position?: {
        top?: number;
        left?: number;
        bottom?: number;
        right?: number;
    },
    offset: number = 10
) {
    const [coords, setCoords] = useState<PlacementResult>({});
    const prevRect = useRef<DOMRect | null>(null);
    const rafId = useRef<number | null>(null);

    useEffect(() => {
        // get target element by id
        const target = document.getElementById(targetId) as HTMLElement | null;
        const modal = document.getElementById(modalId) as HTMLElement | null;
        if (!target || !modal || !open) return;
        else if (pinned) {
            setCoords(position as PlacementResult || {})
            return
        }


        const calculate = () => {
            const t = target.getBoundingClientRect();
            const m = modal.getBoundingClientRect();
            const screenWidth = window.innerWidth;

            let top: number, left: number, bottom: number, right: number;
            // TOP placement
            if (placement === "top") {
                top = t.top - m.height - offset;
                left = t.left;

                if (left < 10) left = 10;
                if (left + m.width > screenWidth - 10)
                    left = screenWidth - m.width - 10;

                setCoords({ top, left });
            }

            // BOTTOM placement
            if (placement === "bottom") {
                bottom = window.innerHeight - (t.bottom + m.height + offset);
                right = window.innerWidth - t.right;

                if (right < 10) right = 10;
                if (right + m.width > screenWidth - 10)
                    right = screenWidth - m.width - 10;

                setCoords({ bottom, right });
            }
        };
        calculate()
        const resizeObserver = new ResizeObserver(() => {
            calculate();
        });
        resizeObserver.observe(modal);

        const loop = () => {
            if (pinned) {
                setCoords(position || {})
                return
            };
            const rect = target.getBoundingClientRect();

            // Rect changed?
            if (
                !prevRect.current ||
                rect.top !== prevRect.current.top ||
                rect.left !== prevRect.current.left ||
                rect.width !== prevRect.current.width ||
                rect.height !== prevRect.current.height
            ) {
                calculate();
                prevRect.current = rect;
            }

            rafId.current = requestAnimationFrame(loop);
        };

        // Start loop
        rafId.current = requestAnimationFrame(loop);

        // Cleanup
        return () => {
            resizeObserver.disconnect();
            if (rafId.current) cancelAnimationFrame(rafId.current);
        };
    }, [targetId, modalId, placement, open, pinned, offset, position]);

    return coords;
}
